import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import TaskSkeleton from "./TaskSkeleton";
import ExpenseSearchSelect, { mapToSelectOptions } from "./ExpenseSearchSelect";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./ExpenseModule.css";
import {
    FY_MONTHS,
    buildFinancialYearOptions,
    formatFinancialYear,
    getFinancialYearStart,
} from "../utils/financialYear";

const NAVY = "#1a237e";
const NAVY_RGB = [26, 35, 126];

const fmt = (v) => Math.round(parseFloat(v || 0)).toLocaleString("en-IN");
const fmtRemaining = (v) => (v === null || v === undefined ? "—" : fmt(v));

export default function ExpenseCategoryReport() {
    const { http, user } = AuthUser();
    const location = useLocation();
    const [heads, setHeads] = useState([]);
    const [itemMasters, setItemMasters] = useState([]);
    const [report, setReport] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [headId, setHeadId] = useState("");
    const [itemMasterId, setItemMasterId] = useState("");
    const [year, setYear] = useState(getFinancialYearStart());
    const [month, setMonth] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const yearOptions = buildFinancialYearOptions();

    useEffect(() => {
        http.get(`/expense_heads?login_user=${user.id}`)
            .then((res) => { if (res.data.success) setHeads(res.data.data || []); })
            .catch(() => toast.error("Error loading categories"));

        http.get(`/expense_item_masters?login_user=${user.id}`)
            .then((res) => { if (res.data.success) setItemMasters(res.data.data || []); })
            .catch(() => toast.error("Error loading items"));
    }, []);

    const fetchReport = async () => {
        setIsLoading(true);
        try {
            let url = `/expense_category_report?login_user=${user.id}&year=${year}`;
            if (headId) url += `&head_id=${headId}`;
            if (itemMasterId) url += `&item_master_id=${itemMasterId}`;
            if (month) url += `&month=${month}`;
            if (dateFrom) url += `&date_from=${dateFrom}`;
            if (dateTo) url += `&date_to=${dateTo}`;
            const res = await http.get(url);
            if (res.data.success) {
                setReport(res.data);
            }
        } catch (err) {
            toast.error("Error loading category report");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchReport(); }, [location.pathname]);

    const filteredItemMasters = useMemo(() => {
        if (!headId) return itemMasters;
        return itemMasters.filter((item) => String(item.expense_head_id) === String(headId));
    }, [itemMasters, headId]);

    useEffect(() => {
        if (!itemMasterId) return;
        const stillValid = filteredItemMasters.some((item) => String(item.id) === String(itemMasterId));
        if (!stillValid) setItemMasterId("");
    }, [filteredItemMasters, itemMasterId]);

    const clearExtraFilters = () => {
        setHeadId("");
        setItemMasterId("");
        setMonth("");
        setDateFrom("");
        setDateTo("");
    };

    const hasExtraFilters = Boolean(headId || itemMasterId || month || dateFrom || dateTo);

    const exportCsv = () => {
        if (!report?.categories?.length) {
            toast.error("No data to export");
            return;
        }
        const headers = ["Category", "Budget", "Spent", "Remaining", "Lines", "Usage %", "Status"];
        const rows = report.categories.map((row) => [
            row.name || "",
            fmt(row.budget),
            fmt(row.spent),
            row.remaining === null || row.remaining === undefined ? "" : fmt(row.remaining),
            row.line_count ?? "",
            row.usage_pct ?? "",
            row.over_budget ? "Over Budget" : "OK",
        ]);
        const csv = [headers, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `Expense_Category_Report_${report.financial_year_label}${month ? `_${report.month_name}` : ""}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success("CSV downloaded");
    };

    const exportPdf = () => {
        if (!report?.categories?.length) {
            toast.error("No data to export");
            return;
        }
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pageW = doc.internal.pageSize.getWidth();
        doc.setFontSize(14);
        doc.setTextColor(...NAVY_RGB);
        doc.text("Expense Category Report", pageW / 2, 14, { align: "center" });
        doc.setFontSize(9);
        doc.setTextColor(80);
        const filterBits = [
            `FY: ${report.financial_year_label}`,
            `Category: ${report.category_name}`,
            `Item: ${report.item_name || "All Items"}`,
            `Month: ${report.month_name}`,
        ];
        if (report.date_from || report.date_to) {
            filterBits.push(`Date: ${report.date_from || "…"} to ${report.date_to || "…"}`);
        }
        doc.text(filterBits.join(" | "), pageW / 2, 20, { align: "center" });
        doc.text(
            `Budget: ${fmt(report.budget)} | Spent: ${fmt(report.spent)} | Remaining: ${fmtRemaining(report.remaining)}`,
            pageW / 2,
            25,
            { align: "center" }
        );

        const body = report.categories.map((row, idx) => [
            idx + 1,
            row.name || "—",
            fmt(row.budget),
            fmt(row.spent),
            fmtRemaining(row.remaining),
            row.line_count ?? 0,
            `${row.usage_pct || 0}%`,
            row.over_budget ? "Over Budget" : "OK",
        ]);

        autoTable(doc, {
            head: [["#", "Category", "Budget", "Spent", "Remaining", "Lines", "Usage", "Status"]],
            body,
            startY: 30,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: NAVY_RGB, textColor: 255 },
            columnStyles: {
                2: { halign: "right" },
                3: { halign: "right" },
                4: { halign: "right" },
                5: { halign: "right" },
                6: { halign: "right" },
            },
        });

        doc.save(`Expense_Category_Report_${report.financial_year_label}${month ? `_${report.month_name}` : ""}.pdf`);
        toast.success("PDF downloaded");
    };

    const categoryOptions = mapToSelectOptions(heads);
    const itemOptions = mapToSelectOptions(filteredItemMasters);
    const yearOptionsList = yearOptions.map((y) => ({ value: String(y), label: formatFinancialYear(y) }));
    const monthOptions = FY_MONTHS.map((m) => ({ value: String(m.value), label: m.label }));

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-3 px-3 pb-4 expense-module">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div>
                        <h5 className="fw-bold mb-0">Expense Category Report</h5>
                        <small className="text-muted">Category-wise budget vs spent summary (Apr – Mar)</small>
                    </div>
                    {report?.categories?.length > 0 && (
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={exportPdf}>
                                <i className="fas fa-file-pdf me-1"></i>PDF
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-success" onClick={exportCsv}>
                                <i className="fas fa-file-csv me-1"></i>CSV
                            </button>
                        </div>
                    )}
                </div>

                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-auto" style={{ minWidth: "170px" }}>
                                <label className="form-label small fw-semibold mb-1">Category</label>
                                <ExpenseSearchSelect
                                    options={categoryOptions}
                                    value={headId}
                                    onChange={setHeadId}
                                    placeholder="Search category..."
                                    isClearable
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "170px" }}>
                                <label className="form-label small fw-semibold mb-1">Item</label>
                                <ExpenseSearchSelect
                                    options={itemOptions}
                                    value={itemMasterId}
                                    onChange={setItemMasterId}
                                    placeholder="Search item..."
                                    isClearable
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "140px" }}>
                                <label className="form-label small fw-semibold mb-1">Financial Year</label>
                                <ExpenseSearchSelect
                                    options={yearOptionsList}
                                    value={String(year)}
                                    onChange={setYear}
                                    placeholder="Search year..."
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "140px" }}>
                                <label className="form-label small fw-semibold mb-1">Month</label>
                                <ExpenseSearchSelect
                                    options={monthOptions}
                                    value={month}
                                    onChange={setMonth}
                                    placeholder="Search month..."
                                    isClearable
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "145px" }}>
                                <label className="form-label small fw-semibold mb-1">From Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={dateFrom}
                                    max={dateTo || undefined}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "145px" }}>
                                <label className="form-label small fw-semibold mb-1">To Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={dateTo}
                                    min={dateFrom || undefined}
                                    onChange={(e) => setDateTo(e.target.value)}
                                />
                            </div>
                            <div className="col-auto">
                                <button className="btn btn-sm btn-primary" onClick={fetchReport}>
                                    <i className="fas fa-filter me-1"></i>Filter
                                </button>
                            </div>
                            {hasExtraFilters && (
                                <div className="col-auto">
                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearExtraFilters}>
                                        <i className="fas fa-times me-1"></i>Clear
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {isLoading ? <TaskSkeleton /> : report && (
                    <>
                        <div className="row g-3 mb-3">
                            {[
                                { label: "Financial Year", value: report.financial_year_label, color: NAVY, bg: "#e8eaf6", icon: "fa-calendar" },
                                { label: "Category", value: report.category_name, color: "#1565c0", bg: "#e3f2fd", icon: "fa-tags" },
                                {
                                    label: "Item",
                                    value: report.item_name || "All Items",
                                    color: "#00838f",
                                    bg: "#e0f7fa",
                                    icon: "fa-box",
                                },
                                {
                                    label: month ? "Month Budget" : "Year Budget",
                                    value: fmt(report.budget),
                                    hint: !month && report.year_budget_raw <= 0 && report.monthly_budget_total > 0
                                        ? "From monthly budgets"
                                        : null,
                                    color: "#1565c0",
                                    bg: "#e3f2fd",
                                    icon: "fa-wallet",
                                },
                                {
                                    label: month ? "Month Spent" : "Year Spent",
                                    value: fmt(report.spent),
                                    color: "#ef6c00",
                                    bg: "#fff3e0",
                                    icon: "fa-chart-line",
                                },
                                {
                                    label: "Remaining",
                                    value: fmtRemaining(report.remaining),
                                    color: report.over_budget ? "#c62828" : "#2e7d32",
                                    bg: report.over_budget ? "#ffebee" : "#e8f5e9",
                                    icon: report.over_budget ? "fa-exclamation-triangle" : "fa-piggy-bank",
                                    badge: report.over_budget ? "Over Budget" : null,
                                },
                            ].map((kpi) => (
                                <div className="col-sm-6 col-xl" key={kpi.label}>
                                    <div className="card border-0 shadow-sm h-100" style={{ background: kpi.bg }}>
                                        <div className="card-body py-3 d-flex align-items-center gap-3">
                                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 40, height: 40, background: "#fff", color: kpi.color }}>
                                                <i className={`fas ${kpi.icon}`}></i>
                                            </div>
                                            <div>
                                                <div className="small text-muted">{kpi.label}</div>
                                                <div className="fw-bold" style={{ color: kpi.color }}>
                                                    {kpi.value}
                                                    {kpi.badge && <span className="badge bg-danger ms-2 small">{kpi.badge}</span>}
                                                </div>
                                                {kpi.hint && <div className="small text-muted">{kpi.hint}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="card border-0 shadow-sm">
                            <div
                                className="card-header py-1 px-3 fw-semibold"
                                style={{ background: NAVY, color: "#fff", fontSize: "0.8rem" }}
                            >
                                Category Summary ({report.total_categories})
                            </div>
                            <div className="table-responsive">
                                <table className="table table-sm table-hover mb-0 align-middle">
                                    <thead>
                                        <tr style={{ background: "#f5f5f5" }}>
                                            <th className="ps-3">#</th>
                                            <th>Category</th>
                                            <th className="text-end">Budget</th>
                                            <th className="text-end">Spent</th>
                                            <th className="text-end">Remaining</th>
                                            <th className="text-center">Lines</th>
                                            <th style={{ minWidth: 120 }}>Usage</th>
                                            <th className="pe-3">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.categories.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className="text-center text-muted py-4">
                                                    No category data found for selected filters.
                                                </td>
                                            </tr>
                                        ) : report.categories.map((row, idx) => (
                                            <tr key={row.id} className={row.over_budget ? "table-danger" : ""}>
                                                <td className="ps-3 text-muted">{idx + 1}</td>
                                                <td className="fw-semibold">{row.name}</td>
                                                <td className="text-end">{fmt(row.budget)}</td>
                                                <td className="text-end fw-bold">{fmt(row.spent)}</td>
                                                <td className={`text-end fw-semibold ${row.over_budget ? "text-danger" : "text-success"}`}>
                                                    {fmtRemaining(row.remaining)}
                                                </td>
                                                <td className="text-center">{row.line_count}</td>
                                                <td>
                                                    {row.budget > 0 ? (
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="progress flex-grow-1" style={{ height: 6 }}>
                                                                <div
                                                                    className={`progress-bar ${row.over_budget ? "bg-danger" : ""}`}
                                                                    style={{
                                                                        width: `${row.usage_pct}%`,
                                                                        background: row.over_budget ? undefined : NAVY,
                                                                    }}
                                                                ></div>
                                                            </div>
                                                            <span className="small text-muted" style={{ minWidth: 32 }}>{row.usage_pct}%</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </td>
                                                <td className="pe-3">
                                                    {row.over_budget ? (
                                                        <span className="badge bg-danger">Over Budget</span>
                                                    ) : (
                                                        <span className="badge bg-success">OK</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    {report.categories.length > 0 && (
                                        <tfoot>
                                            <tr style={{ background: "#fff8e1" }}>
                                                <td colSpan={2} className="text-end fw-bold ps-3 py-2">Total</td>
                                                <td className="text-end fw-bold py-2">{fmt(report.budget)}</td>
                                                <td className="text-end fw-bold py-2">{fmt(report.spent)}</td>
                                                <td className={`text-end fw-bold py-2 ${report.over_budget ? "text-danger" : "text-success"}`}>
                                                    {fmtRemaining(report.remaining)}
                                                </td>
                                                <td className="text-center fw-bold py-2">
                                                    {report.categories.reduce((sum, row) => sum + (row.line_count || 0), 0)}
                                                </td>
                                                <td colSpan={2} className="pe-3"></td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
